import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../styles/DocumentList.module.css";
import { FaArrowLeft, FaSearch, FaDownload, FaFilter, FaCalculator, FaFilePdf } from "react-icons/fa";
import { useAppContext } from "../context";
import FilterPopup from "../components/FilterPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter as faFilterIcon } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

export default function DocumentList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCategory, searchText, setSearchText, filter, setFilter, setSelectedCategory } = useAppContext();
  const [showFilter, setShowFilter] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Track if we came from a tab click or filter apply
  const navigationSource = useRef(null);

  // When component mounts, check if we have selectedCategory from context
  useEffect(() => {
    // If we have a selectedCategory (from tab click) and no filter is applied
    if (selectedCategory && !filter.mainCategory) {
      navigationSource.current = 'tab';
    }
    // If we have filter applied
    else if (filter.mainCategory) {
      navigationSource.current = 'filter';
      // Ensure selectedCategory matches filter
      if (selectedCategory !== filter.mainCategory) {
        setSelectedCategory(filter.mainCategory);
      }
    }
    
    if (searchText) setSearchTerm(searchText);
  }, []);

  useEffect(() => {
    if (searchText) setSearchTerm(searchText);
  }, [searchText]);

  useEffect(() => {
    fetchDocuments();
  }, [selectedCategory, filter, searchTerm]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // Priority: filter takes precedence over selectedCategory if both exist
      const effectiveCategory = filter.mainCategory || selectedCategory || '';
      
      const params = {
        category: effectiveCategory,
        subCategory: filter.subCategory || '',
        subSubCategory: filter.subSubCategory || '',
        search: searchTerm || ''
      };

      const response = await axios.get("https://tph-api-server2.vishvaconnect.site/getDocuments.php", { params });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    // Reset everything when going back
    setFilter({
      mainCategory: "",
      subCategory: "",
      subSubCategory: "",
    });
    setSelectedCategory(null);
    setSearchText("");
    navigate("/");
  };

  const handleCalculatorClick = () => {
    navigate("/area-convertor");
  };

  const filteredDocs = documents.filter(doc =>
    searchTerm.trim() === "" || doc.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDocumentClick = (doc) => {
    // Extract the actual PDF URL from the Google Docs viewer URL if needed
    let pdfUrl = doc.downloadLink;

    // If it's already a Google Docs URL, extract the original PDF URL
    if (pdfUrl && pdfUrl.includes('docs.google.com/gview')) {
      try {
        const urlParams = new URLSearchParams(new URL(pdfUrl).search);
        pdfUrl = urlParams.get('url');
      } catch (e) {
        console.error("Error parsing URL:", e);
      }
    }

    // Navigate to PDF viewer with the document data
    navigate('/pdf-viewer', {
      state: {
        pdfUrl: pdfUrl,
        title: doc.title
      }
    });
  };

  // Get display category name
  const getDisplayCategory = () => {
    if (filter.mainCategory) return filter.mainCategory;
    if (selectedCategory) return selectedCategory;
    return "All Documents";
  };

  return (
    <div className={styles.documentListScreenContainer}>
      <div className={styles.documentListScreenHeader}>
        <button className={styles.documentListScreenBackButton} onClick={handleBack}>
          <FaArrowLeft />
        </button>
        <div className={styles.documentListScreenSearchContainer}>
          <input
            type="text"
            className={styles.documentListScreenSearchInput}
            placeholder={`Search in ${getDisplayCategory()}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className={styles.documentListScreenSearchButton} onClick={fetchDocuments}>
            <FaSearch />
          </button>
        </div>
      </div>

      <h2 className={styles.documentListScreenTitle}>
        {filteredDocs.length} Documents in {getDisplayCategory()}
        {filter.subCategory && ` > ${filter.subCategory}`}
        {filter.subSubCategory && ` > ${filter.subSubCategory}`}
      </h2>

      <div className={styles.documentListScreenList}>
        {loading ? (
          <p>Loading documents...</p>
        ) : filteredDocs.length === 0 ? (
          <p>No documents found.</p>
        ) : (
          filteredDocs.map((doc) => (
            <div
              key={doc.srNo || doc.id || Math.random()}
              onClick={() => handleDocumentClick(doc)}
              className={styles.documentListScreenCard}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.documentListScreenIconBox}>
                <FaFilePdf />
              </div>
              <div className={styles.documentListScreenDocInfo}>
                <p className={styles.documentListScreenDocTitle}>{doc.title}</p>
                <p className={styles.documentListScreenDocDate}>Date: {doc.date}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <button
        className={`${styles.documentListScreenFloatIcon} ${styles.documentListScreenCalculator}`}
        onClick={handleCalculatorClick}
      >
        <FaCalculator />
      </button>

      <button
        className={`${styles.documentListScreenFloatIcon} ${styles.documentListScreenFilter}`}
        onClick={() => setShowFilter(true)}
      >
        <FontAwesomeIcon icon={faFilterIcon} />
      </button>

      {showFilter && <FilterPopup onClose={() => setShowFilter(false)} />}
    </div>
  );
}